import { MarkdownContent } from '@backstage/core-components';
import { useApi } from '@backstage/frontend-plugin-api';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
} from '@material-ui/core';
import { Tooltip as BuiTooltip, TooltipTrigger } from '@backstage/ui';
import { Focusable } from 'react-aria-components';
import AddCommentIcon from '@material-ui/icons/AddComment';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import MenuIcon from '@material-ui/icons/Menu';
import MinimizeIcon from '@material-ui/icons/Minimize';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ThumbDownAltOutlinedIcon from '@material-ui/icons/ThumbDownAltOutlined';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbUpAltOutlinedIcon from '@material-ui/icons/ThumbUpAltOutlined';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { MainFeedbackRequest } from '../../alma-sdk';
import AlmaLogo from '../../alma-icon.png';
import { almaApiRef, MainConversationSummary } from '../../types';
import { isInternalUrl, getPath, getPathTail } from '../../utils/urlUtils';

import { type AlmaMessage } from './almaTypes';
import {
  DRAWER_WIDTH_DEFAULT,
  DRAWER_WIDTH_MIN,
  DRAWER_WIDTH_MAX,
  PANEL_WIDTH_MIN,
  PANEL_WIDTH_MAX,
  PANEL_HEIGHT_MIN,
  PANEL_HEIGHT_MAX,
  PANEL_WIDTH_DEFAULT,
  PANEL_HEIGHT_DEFAULT,
  RECENT_CONVERSATIONS_LIMIT,
} from './constants';
import { useStyles } from './styles';
import {
  askAlma,
  feedbackAlma,
  getRecentConversations,
  getConversationById,
  getUniqueReferences,
  extractArticleContent,
  normalizeRestoredMessage,
  formatConversationLabel,
  formatConversationUpdatedAt,
} from './helpers';
import { AlmaQueryClientProvider } from './QueryClientProvider';

// ---- Main Component ----

export function AlmaPopUp() {
  return (
    <AlmaQueryClientProvider>
      <AlmaPopUpInner />
    </AlmaQueryClientProvider>
  );
}

function AlmaPopUpInner() {
  const almaApi = useApi(almaApiRef);
  const classes = useStyles();

  const [conversationId, setConversationId] = useState<number>(0);
  const askMutation = useMutation({
    mutationFn: (params: {
      question: string;
      conversationId: number;
      setId: (id: number) => void;
      pageContext?: string;
    }) =>
      askAlma(
        almaApi,
        params.question,
        params.conversationId,
        params.setId,
        params.pageContext,
      ),
  });
  const feedbackMutation = useMutation({
    mutationFn: (data: MainFeedbackRequest) => feedbackAlma(almaApi, data),
  });

  const [open, setOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<AlmaMessage[]>([]);
  const [typing, setTyping] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const restoringRef = useRef(false);
  const [recentConversations, setRecentConversations] = useState<
    MainConversationSummary[]
  >([]);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [pendingRemove, setPendingRemove] =
    useState<MainConversationSummary | null>(null);
  const [writeMode, setWriteMode] = useState(false);
  const [panelWidth, setPanelWidth] = useState(PANEL_WIDTH_DEFAULT);
  const [panelHeight, setPanelHeight] = useState(PANEL_HEIGHT_DEFAULT);
  const [includePageContext, setIncludePageContext] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_WIDTH_DEFAULT);
  const [isDragging, setIsDragging] = useState(false);

  const resizing = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const startWidth = useRef(PANEL_WIDTH_DEFAULT);
  const startHeight = useRef(PANEL_HEIGHT_DEFAULT);
  const drawerResizing = useRef(false);
  const drawerStartX = useRef(0);
  const drawerStartWidth = useRef(DRAWER_WIDTH_DEFAULT);
  const wasAutoCollapsed = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Resize
  const handleResizeStart = (e: React.MouseEvent) => {
    resizing.current = true;
    startX.current = e.clientX;
    startY.current = e.clientY;
    startWidth.current = panelWidth;
    startHeight.current = panelHeight;
    setIsDragging(true);
    document.body.style.userSelect = 'none';
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (resizing.current) {
        setPanelWidth(
          Math.max(
            PANEL_WIDTH_MIN,
            Math.min(
              PANEL_WIDTH_MAX,
              startWidth.current - (e.clientX - startX.current),
            ),
          ),
        );
        setPanelHeight(
          Math.max(
            PANEL_HEIGHT_MIN,
            Math.min(
              PANEL_HEIGHT_MAX,
              startHeight.current - (e.clientY - startY.current),
            ),
          ),
        );
      }
      if (drawerResizing.current) {
        const newWidth =
          drawerStartWidth.current + (e.clientX - drawerStartX.current);
        const maxAllowed = panelWidth - PANEL_WIDTH_MIN;
        setDrawerWidth(
          Math.max(
            DRAWER_WIDTH_MIN,
            Math.min(DRAWER_WIDTH_MAX, maxAllowed, newWidth),
          ),
        );
      }
    },
    [panelWidth],
  );

  const handleMouseUp = useCallback(() => {
    resizing.current = false;
    drawerResizing.current = false;
    setIsDragging(false);
    document.body.style.userSelect = '';
  }, []);

  const handleDrawerResizeStart = (e: React.MouseEvent) => {
    drawerResizing.current = true;
    drawerStartX.current = e.clientX;
    drawerStartWidth.current = drawerWidth;
    setIsDragging(true);
    document.body.style.userSelect = 'none';
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (fullscreen) return;
    if (drawerOpen) {
      const chatSpace = panelWidth - drawerWidth;
      if (chatSpace < PANEL_WIDTH_MIN) {
        const needed = panelWidth - PANEL_WIDTH_MIN;
        if (needed >= DRAWER_WIDTH_MIN) {
          setDrawerWidth(needed);
        } else {
          wasAutoCollapsed.current = true;
          setDrawerOpen(false);
        }
      }
    } else if (wasAutoCollapsed.current) {
      const available = panelWidth - PANEL_WIDTH_MIN;
      if (available >= DRAWER_WIDTH_MIN) {
        wasAutoCollapsed.current = false;
        setDrawerWidth(
          Math.min(available, DRAWER_WIDTH_DEFAULT, DRAWER_WIDTH_MAX),
        );
        setDrawerOpen(true);
      }
    }
  }, [panelWidth, drawerWidth, fullscreen, drawerOpen]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Load conversations when drawer opens
  useEffect(() => {
    if (!drawerOpen) return;
    (async () => {
      const result = await getRecentConversations(
        almaApi,
        RECENT_CONVERSATIONS_LIMIT,
      );
      setRecentConversations(result?.conversations ?? []);
    })();
  }, [drawerOpen, almaApi, conversationId]);

  const restoreConversationById = useCallback(
    async (selectedId: number) => {
      if (restoringRef.current) return;
      restoringRef.current = true;
      setRestoring(true);
      try {
        const conversation = await getConversationById(almaApi, selectedId);
        if (
          !conversation ||
          !conversation.conversationId ||
          !Array.isArray(conversation.messages) ||
          conversation.messages.length === 0
        )
          return;
        setConversationId(conversation.conversationId);
        setMessages(
          conversation.messages
            .map((msg: { role: string; content: string }, idx: number) =>
              normalizeRestoredMessage(msg, idx),
            )
            .filter((msg): msg is AlmaMessage => msg !== null),
        );
      } finally {
        restoringRef.current = false;
        setRestoring(false);
      }
    },
    [almaApi],
  );

  const startNewChat = useCallback(() => {
    if (askMutation.isPending || restoring) return;
    setConversationId(0);
    setMessages([]);
    setInput('');
    setTyping(false);
  }, [askMutation.isPending, restoring]);

  const requestRemove = useCallback((c: MainConversationSummary) => {
    setPendingRemove(c);
    setRemoveDialogOpen(true);
  }, []);

  const cancelRemove = useCallback(() => {
    setRemoveDialogOpen(false);
    setPendingRemove(null);
  }, []);

  const confirmRemove = useCallback(async () => {
    if (!pendingRemove) return;
    const id = pendingRemove.conversationId;
    const result = await almaApi.deleteConversation(id);
    if (!result.ok) {
      cancelRemove();
      return;
    }
    setRecentConversations(prev => prev.filter(x => x.conversationId !== id));
    setRemoveDialogOpen(false);
    setPendingRemove(null);
    if (conversationId === id) {
      setConversationId(0);
      setMessages([]);
      setInput('');
      setTyping(false);
    }
  }, [pendingRemove, conversationId, almaApi, cancelRemove]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.key === 'Escape') {
        if (writeMode) inputRef.current?.blur();
        else inputRef.current?.focus();
        return;
      }
      if (e.key === 'Enter') {
        inputRef.current?.focus();
        return;
      }
      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        if (e.code === 'KeyA') {
          e.preventDefault();
          setOpen(p => !p);
        } else if (e.code === 'KeyF') {
          e.preventDefault();
          setFullscreen(f => !f);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [writeMode]);

  const handleSubmit = () => {
    if (askMutation.isPending) return;
    const value = input.trim();
    if (!value) return;
    setMessages(prev => [
      ...prev,
      { id: Date.now(), text: value, isUser: true, feedback: null },
    ]);
    setInput('');
    handleAlmaReply(value);
  };

  async function handleAlmaReply(question: string) {
    setTyping(true);
    try {
      const pageContent = includePageContext
        ? extractArticleContent()
        : undefined;
      const reply = await askMutation.mutateAsync({
        question,
        conversationId,
        setId: setConversationId,
        pageContext: pageContent,
      });
      setMessages(prev => [
        ...prev,
        {
          id: Date.now(),
          text: reply?.answer || 'No response',
          isUser: false,
          feedback: null,
          references: Array.isArray(reply?.references) ? reply.references : [],
        },
      ]);
    } catch {
      setMessages(prev => [
        ...prev,
        {
          id: Date.now(),
          text: 'Something went wrong. Please try again.',
          isUser: false,
          feedback: null,
          references: [],
        },
      ]);
    } finally {
      setTyping(false);
    }
  }

  function findLastUserQuestionBefore(id: number) {
    const userMsgs = messages.filter(x => x.isUser && x.id < id);
    return userMsgs.length ? userMsgs[userMsgs.length - 1].text : '';
  }

  async function sendFeedback(rating: string, message: AlmaMessage) {
    await feedbackMutation.mutateAsync({
      rating,
      description: '',
      question: findLastUserQuestionBefore(message.id),
      answer: message.text,
      timestamp: new Date().toISOString(),
    });
  }

  const toggleFeedback = (id: number, value: 'up' | 'down') => {
    setMessages(prev =>
      prev.map(m => {
        if (m.id !== id) return m;
        const next = m.feedback === value ? null : value;
        if (next) sendFeedback(next, m);
        return { ...m, feedback: next };
      }),
    );
  };

  // ---- Render ----

  if (!open) {
    return (
      <div className={classes.root}>
        <TooltipTrigger>
          <Focusable>
            <div
              className={classes.launcherTab}
              onClick={() => setOpen(true)}
              role="button"
              tabIndex={0}
              aria-label="Open Alma chat"
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') setOpen(true);
              }}
            >
              <span className={classes.letterLavenderDark}>A</span>
              <span className={classes.letterLavenderLight}>L</span>
              <span className={classes.letterBalticDark}>M</span>
              <span className={classes.letterBalticLight}>A</span>
            </div>
          </Focusable>
          <BuiTooltip placement="top">Open Alma</BuiTooltip>
        </TooltipTrigger>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Paper
        elevation={12}
        aria-label="Alma chat"
        className={`${classes.panel} ${fullscreen ? classes.panelFullscreen : classes.panelNormal} ${isDragging ? classes.panelDragging : ''}`}
        style={
          fullscreen ? undefined : { width: panelWidth, height: panelHeight }
        }
      >
        <button
          type="button"
          aria-label="Resize chat widget"
          className={classes.resizeHandle}
          disabled={fullscreen}
          onMouseDown={handleResizeStart}
        />

        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <img src={AlmaLogo} alt="Alma" className={classes.almaLogoSmall} />
          </div>
          <div className={classes.headerActions}>
            <MuiTooltip title="New conversation">
              <span>
                <IconButton
                  className={classes.headerActionIcon}
                  disabled={askMutation.isPending || restoring}
                  onClick={startNewChat}
                  size="small"
                  aria-label="New chat"
                >
                  <AddCommentIcon fontSize="small" />
                </IconButton>
              </span>
            </MuiTooltip>
            <MuiTooltip title={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <IconButton
                className={classes.headerActionIcon}
                size="small"
                onClick={() => setFullscreen(f => !f)}
                aria-label="Toggle fullscreen"
              >
                {fullscreen ? (
                  <FullscreenExitIcon fontSize="small" />
                ) : (
                  <FullscreenIcon fontSize="small" />
                )}
              </IconButton>
            </MuiTooltip>
            <MuiTooltip title="Minimize">
              <IconButton
                className={classes.headerActionIcon}
                size="small"
                onClick={() => setOpen(false)}
                aria-label="Minimize"
              >
                <MinimizeIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          </div>
        </div>

        {/* Body = Drawer (left) + Chat (right) */}
        <div className={classes.panelBody}>
          <Drawer
            variant="persistent"
            anchor="left"
            open={drawerOpen}
            PaperProps={{
              className: `${classes.drawerPaper} ${isDragging ? classes.drawerPaperDragging : ''}`,
              style: { width: drawerWidth },
            }}
            ModalProps={{ container: undefined }}
          >
            <div className={classes.drawerHeader}>
              <Typography variant="subtitle2">Conversations</Typography>
              <IconButton
                size="small"
                onClick={() => {
                  wasAutoCollapsed.current = false;
                  setDrawerOpen(false);
                }}
                aria-label="Close sidebar"
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </div>
            <List className={classes.drawerList}>
              {recentConversations.map(c => (
                <ListItem
                  key={c.conversationId}
                  className={`${classes.drawerItem} ${c.conversationId === conversationId ? classes.drawerItemActive : ''}`}
                  onClick={() => void restoreConversationById(c.conversationId)}
                  button
                >
                  <ListItemText
                    primary={formatConversationLabel(c)}
                    secondary={formatConversationUpdatedAt(c.updatedAt)}
                    primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                    secondaryTypographyProps={{
                      noWrap: true,
                      variant: 'caption',
                    }}
                  />
                  <IconButton
                    size="small"
                    className={classes.drawerDeleteBtn}
                    onClick={e => {
                      e.stopPropagation();
                      requestRemove(c);
                    }}
                    aria-label="Delete conversation"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))}
              {recentConversations.length === 0 && (
                <ListItem disabled>
                  <ListItemText primary="No conversations yet" />
                </ListItem>
              )}
            </List>
            <button
              type="button"
              aria-label="Resize conversations panel"
              className={classes.drawerResizeHandle}
              onMouseDown={handleDrawerResizeStart}
            />
          </Drawer>

          {/* Main chat column */}
          <div
            className={classes.mainColumn}
            style={
              drawerOpen
                ? {
                    marginLeft: drawerWidth,
                    ...(isDragging ? { transition: 'none' } : {}),
                  }
                : undefined
            }
          >
            {!drawerOpen && (
              <MuiTooltip title="Recent conversations" placement="right">
                <IconButton
                  className={`${classes.floatingBtn} ${classes.hamburgerBtn}`}
                  size="small"
                  onClick={() => {
                    wasAutoCollapsed.current = false;
                    const needed = drawerWidth + PANEL_WIDTH_MIN;
                    if (!fullscreen && panelWidth < needed) {
                      setPanelWidth(Math.min(needed, PANEL_WIDTH_MAX));
                    }
                    setDrawerOpen(true);
                  }}
                  aria-label="Open conversation sidebar"
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              </MuiTooltip>
            )}
            <div ref={scrollRef} className={classes.content} aria-live="polite">
              {messages.map(m =>
                m.isUser ? (
                  <div
                    key={m.id}
                    className={`${classes.message} ${classes.userMsg}`}
                  >
                    <Typography
                      variant="body2"
                      className={classes.userMessageText}
                    >
                      {m.text}
                    </Typography>
                  </div>
                ) : (
                  <div key={m.id} className={classes.botMsgWrap}>
                    <div className={`${classes.message} ${classes.botMsg}`}>
                      <div className={classes.markdownScroll}>
                        <div className={classes.markdownContent}>
                          <MarkdownContent content={m.text} />
                        </div>
                      </div>
                      {Array.isArray(m.references) &&
                        m.references.length > 0 && (
                          <div className={classes.referencesSection}>
                            <Typography
                              variant="caption"
                              className={classes.referencesTitle}
                            >
                              References:
                            </Typography>
                            <ul className={classes.referencesList}>
                              {getUniqueReferences(m.references ?? []).map(
                                r => {
                                  let url: string | undefined;
                                  let scoreVal: number | undefined;
                                  if ('url' in r) {
                                    url = r.url;
                                    scoreVal = r.score;
                                  } else if ('Url' in r) {
                                    url = r.Url;
                                    scoreVal = r.Score;
                                  }
                                  if (typeof url !== 'string') return null;
                                  return (
                                    <li
                                      key={url}
                                      className={classes.referenceItem}
                                    >
                                      {isInternalUrl(url) ? (
                                        <MuiTooltip title={url} placement="top">
                                          <Link
                                            component={RouterLink}
                                            to={getPath(url)}
                                            underline="hover"
                                            className={classes.referenceLink}
                                            aria-label={`Reference: ${url}`}
                                          >
                                            {`Aura Portal: ${getPathTail(getPath(url))}`}
                                          </Link>
                                        </MuiTooltip>
                                      ) : (
                                        <MuiTooltip title={url} placement="top">
                                          <Link
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            className={classes.referenceLink}
                                            aria-label={`Reference: ${url} (opens in new tab)`}
                                          >
                                            <OpenInNewIcon
                                              className={
                                                classes.externalLinkIcon
                                              }
                                            />
                                            {url}
                                          </Link>
                                        </MuiTooltip>
                                      )}
                                      {typeof scoreVal === 'number' && (
                                        <MuiTooltip
                                          arrow
                                          title={
                                            <span>
                                              Matching Score{' '}
                                              {(scoreVal * 100).toFixed(1)}%.
                                              <br />
                                              Shows how closely the reference
                                              text is to your question.
                                              <br />
                                              High similarity does not guarantee
                                              correctness but indicates
                                              relevance.
                                            </span>
                                          }
                                        >
                                          <Typography
                                            component="span"
                                            variant="caption"
                                            className={classes.referenceScore}
                                            aria-label={`Matching Score ${(scoreVal * 100).toFixed(1)}%`}
                                          >
                                            {(scoreVal * 100).toFixed(0)}%
                                          </Typography>
                                        </MuiTooltip>
                                      )}
                                    </li>
                                  );
                                },
                              )}
                            </ul>
                          </div>
                        )}
                      <div
                        className={classes.feedbackBar}
                        aria-label="Feedback"
                      >
                        <Typography
                          variant="caption"
                          className={classes.feedbackText}
                        >
                          feedback
                        </Typography>
                        <MuiTooltip
                          title={
                            m.feedback === 'up'
                              ? 'Remove upvote'
                              : 'Mark helpful'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => toggleFeedback(m.id, 'up')}
                            aria-label="Upvote"
                            className={`${classes.feedbackBtn} ${m.feedback === 'up' ? classes.feedbackBtnSelectedUp : ''}`}
                          >
                            {m.feedback === 'up' ? (
                              <ThumbUpIcon className={classes.feedbackIcon} />
                            ) : (
                              <ThumbUpAltOutlinedIcon
                                className={classes.feedbackIcon}
                              />
                            )}
                          </IconButton>
                        </MuiTooltip>
                        <MuiTooltip
                          title={
                            m.feedback === 'down'
                              ? 'Remove downvote'
                              : 'Mark not helpful'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => toggleFeedback(m.id, 'down')}
                            aria-label="Downvote"
                            className={`${classes.feedbackBtn} ${m.feedback === 'down' ? classes.feedbackBtnSelectedDown : ''}`}
                          >
                            {m.feedback === 'down' ? (
                              <ThumbDownIcon className={classes.feedbackIcon} />
                            ) : (
                              <ThumbDownAltOutlinedIcon
                                className={classes.feedbackIcon}
                              />
                            )}
                          </IconButton>
                        </MuiTooltip>
                      </div>
                    </div>
                  </div>
                ),
              )}
              {askMutation.error && (
                <Typography
                  variant="body2"
                  color="error"
                  className={classes.chatErrorMessage}
                >
                  {askMutation.error?.message || 'Unknown error'}
                </Typography>
              )}
              {typing && (
                <Typography
                  aria-live="polite"
                  className={classes.typingIndicator}
                  role="status"
                  variant="body2"
                >
                  Alma is thinking&hellip;
                </Typography>
              )}
            </div>

            <div
              className={classes.cautionNote}
              aria-label="AI response caution"
            >
              AI can make mistakes - verify answers before acting on them.
            </div>

            <Divider />
            <div className={classes.inputBar}>
              <TextField
                inputRef={inputRef}
                value={input}
                onFocus={() => setWriteMode(true)}
                onBlur={() => setWriteMode(false)}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="Ask Alma&hellip;"
                fullWidth
                variant="outlined"
                size="small"
                multiline
              />
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disableElevation
              >
                SEND
              </Button>
            </div>
            <div className={classes.pageContextSwitchWrap}>
              <MuiTooltip
                title={
                  includePageContext
                    ? 'Stop including this page\u2019s content in questions'
                    : 'Allow Alma to use content from this page'
                }
              >
                <FormControlLabel
                  className={classes.pageContextLabel}
                  control={
                    <Switch
                      checked={includePageContext}
                      onChange={(_e, checked) => setIncludePageContext(checked)}
                      size="small"
                      color="primary"
                      aria-label="Include content from this page"
                    />
                  }
                  label={
                    <Typography variant="caption" color="textSecondary">
                      Include content from current page
                    </Typography>
                  }
                />
              </MuiTooltip>
            </div>
          </div>
        </div>
      </Paper>

      {/* Delete confirmation dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={cancelRemove}
        aria-labelledby="alma-v2-remove-title"
      >
        <DialogTitle id="alma-v2-remove-title">
          Remove conversation?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete this conversation and its messages.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRemove} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmRemove} color="primary" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
